import {useQuery} from '@apollo/client';
import {Radio, Select, Upload} from 'antd';
import {GET_ALL_USERS_BY_NAME} from 'graphql/queries/campaign';
import {useEffect, useMemo, useRef, useState} from 'react';
import _ from 'lodash';
import {toast} from 'react-toastify';
import {customFilterSelectSearch, sanitizeInput} from 'utils/helper';
import styles from './notificationRecipientSelect.module.scss';
import {
  countRecipients,
  emptyRecipients,
  extractRecipientsFromCsv,
  isUserId,
  normalizeEntry,
  RECIPIENT_MODES,
} from './recipientCsv';

/**
 * What an accepted sheet looks like, shown to the admin rather than described.
 *
 * A header row is optional and extra columns are ignored — the parser reads
 * every cell and keeps the ones shaped like an address or a user id, so a
 * straight export works without reformatting. The sample deliberately mixes
 * both kinds: one sheet can carry addresses and ids together and they are
 * separated into `userEmails` and `userIds` on the way out.
 */
const SAMPLE_CSV = `Recipient
jane@daba.com
john@daba.com
507f1f77bcf86cd799439011`;

/**
 * The same sheet as a file the admin can open in Excel or Sheets and fill in.
 * Built in the browser; nothing is uploaded to produce it.
 */
const downloadTemplate = () => {
  const blob = new Blob([`${SAMPLE_CSV}\n`], {type: 'text/csv;charset=utf-8'});
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'notification-recipients-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Recipient picker for the notification composer.
 *
 * This is a separate component from <UserSelectField> on purpose. That one is
 * mounted in twenty-odd places across the admin — orders, groups, rewards,
 * helpdesk — where the value is always "a list of users the admin picked", and
 * every caller reads ids back out of the same "firstName;lastName;id;email"
 * option key. Notifications now also accept recipients as an array of
 * addresses, resolved server-side, including people the picker never loaded.
 * Adding a second output list to the shared field would change a value shape
 * with nineteen other consumers, so the bulk entry lives here.
 *
 * Two fields, because they answer two different questions:
 *
 *   - The picker, for "who do I mean?" — search by name, the backend resolves
 *     it, and the admin confirms the right person from the results.
 *   - The bulk field, for "here is the list" — addresses and user ids that are
 *     already decided, pasted or read out of a CSV, sent as `userEmails` and
 *     `userIds` for the service to resolve in one indexed query.
 *
 * Names are deliberately not accepted in the second field. An unresolvable
 * name would be dropped server-side without any warning, and the admin would
 * never learn that part of their list did not arrive.
 *
 * `value` is the whole recipient set:
 *   {users, entries, fileName, invalidRows}
 * Convert it at submit time with `buildRecipientPayload`.
 */
export const NotificationRecipientSelect = ({
  value,
  onChange,
  defaultUsers,
  placeholder = 'Search and Select User(s)',
}) => {
  const {Option} = Select;
  const recipients = value || emptyRecipients();
  const [limit, setLimit] = useState(50);
  const [usersData, setUsersData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [hasNextPage, setHasNextPage] = useState(true);
  const [showInvalidRows, setShowInvalidRows] = useState(false);
  const [showFormat, setShowFormat] = useState(false);
  const hasAutoSelected = useRef(false);

  const update = patch => onChange?.({...recipients, ...patch});

  const {networkStatus, refetch, loading, fetchMore} = useQuery(
    GET_ALL_USERS_BY_NAME,
    {
      variables: {
        filters: {
          pagination: {limit},
          user: {admin: false},
        },
      },
      onCompleted: data => {
        setHasNextPage(data?.getAllUsers?.hasNextPage);
        setUsersData(data?.getAllUsers?.users);
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const debouncedSetSearchValue = _.debounce(searchTerm => {
    setSearchValue(searchTerm);
    const sanitizedValue = sanitizeInput(searchTerm);

    if (sanitizedValue === '') return;

    refetch({
      filters: {
        pagination: {limit},
        sort: {order: 'DESC', field: 'createdAt'},
        user: {username: sanitizedValue || null, admin: false},
      },
    });
  }, 1200);

  const onScroll = event => {
    const target = event.target;
    if (!hasNextPage) return;
    if (
      !loading &&
      target.scrollTop + target.offsetHeight === target.scrollHeight
    ) {
      const newLimit = limit + 50;
      setLimit(newLimit);
      fetchMore({
        variables: {
          filters: {
            pagination: {limit: newLimit},
            user: {username: target.value, admin: false},
          },
        },
      });
    }
  };

  // Edit mode opens with the saved recipient ids. Only resolved ids are
  // persisted on a notification — an uploaded sheet names recipients, it is
  // not stored state — so a notification sent from a CSV reopens as users.
  useEffect(() => {
    if (defaultUsers?.length > 0) {
      refetch({
        filters: {user: {userIds: defaultUsers, admin: false}},
      });
    }
  }, [defaultUsers]);

  // Once those ids resolve, mark them selected — but only once, so a later
  // manual clear is not undone, and so the labels read as names not raw ids.
  useEffect(() => {
    if (
      !hasAutoSelected.current &&
      Array.isArray(defaultUsers) &&
      defaultUsers.length &&
      usersData?.length &&
      !recipients.users?.length
    ) {
      const resolved = usersData
        .filter(user => defaultUsers.includes(user?.id))
        .map(
          user =>
            `${user?.firstName};${user?.lastName};${user?.id};${user?.email}`,
        );
      if (resolved.length) {
        hasAutoSelected.current = true;
        update({users: resolved});
      }
    }
  }, [defaultUsers, usersData, recipients.users]);

  const filterOptions = data => {
    const searchTerms = searchValue
      .split(',')
      .map(term => term.trim())
      .filter(term => term);

    if (searchTerms.length === 0) return true;
    return searchTerms.some(term => customFilterSelectSearch(data, term));
  };

  /**
   * Adds entries, keeping the order they arrived in and dropping duplicates.
   * Pasting a list twice, or uploading over a paste, must not double the
   * recipient count.
   */
  const mergeEntries = incoming => {
    const merged = [...(recipients.entries || [])];
    incoming.forEach(entry => {
      if (entry && !merged.includes(entry)) merged.push(entry);
    });
    return merged;
  };

  /**
   * antd hands back whatever was typed, including anything split out on a
   * separator. Each value is validated here rather than at submit time: the
   * mutation flattens every validation failure into one generic message, so a
   * single bad address would otherwise fail the whole send with nothing
   * pointing at which one.
   */
  const handleEntriesChange = values => {
    const accepted = [];
    const rejected = [];

    values.forEach(raw => {
      const entry = normalizeEntry(raw);
      if (entry) {
        if (!accepted.includes(entry)) accepted.push(entry);
        return;
      }
      const shown = String(raw ?? '').trim();
      if (shown) rejected.push(shown);
    });

    if (rejected.length) {
      toast.error(
        `Not an email address or user ID: ${rejected.join(
          ', ',
        )}. Search by name in the field above instead.`,
      );
    }

    update({entries: accepted});
  };

  /**
   * The file never leaves the browser: it is read here and only the extracted
   * addresses and ids are kept. `beforeUpload` returning false keeps antd from
   * attempting its own request.
   */
  const handleFile = file => {
    const reader = new FileReader();
    reader.onerror = () =>
      toast.error('Could not read that file. Please try again.');
    reader.onload = event => {
      const {emails, ids, invalid} = extractRecipientsFromCsv(
        event.target?.result,
      );

      if (!emails.length && !ids.length) {
        toast.error(
          'No email addresses or user IDs found in that file. Check that the addresses are in their own column.',
        );
        return;
      }

      update({
        entries: mergeEntries([...emails, ...ids]),
        fileName: file.name,
        invalidRows: invalid,
      });
      setShowInvalidRows(false);

      if (invalid.length) {
        toast.warning(
          `${invalid.length} row(s) were skipped because they are not a valid email or user ID.`,
        );
      }
    };
    reader.readAsText(file);
    // Tell antd not to upload it anywhere.
    return false;
  };

  /**
   * Dismisses the file notice only. The recipients it contributed are ordinary
   * chips by now and may have been edited, so they are the admin's to remove.
   */
  const clearUpload = () => update({fileName: '', invalidRows: []});

  const mode = recipients.mode || RECIPIENT_MODES.SEARCH;
  const entries = recipients.entries || [];
  const invalidRows = recipients.invalidRows || [];
  const idCount = entries.filter(isUserId).length;
  const emailCount = entries.length - idCount;
  const total = useMemo(() => countRecipients(recipients), [recipients]);

  return (
    <div className={styles.container}>
      <Radio.Group
        className={styles.modes}
        optionType="button"
        buttonStyle="solid"
        value={mode}
        onChange={event => update({mode: event.target.value})}>
        <Radio.Button value={RECIPIENT_MODES.SEARCH}>
          Search for users
        </Radio.Button>
        <Radio.Button value={RECIPIENT_MODES.BULK}>
          Paste or upload a list
        </Radio.Button>
      </Radio.Group>

      {mode === RECIPIENT_MODES.SEARCH ? (
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Search for users by name</span>
          <Select
            mode="multiple"
            value={recipients.users}
            placeholder={placeholder}
            showSearch
            allowClear
            filterOption={false}
            onPopupScroll={onScroll}
            onSearch={term => debouncedSetSearchValue(term)}
            onChange={users => update({users})}>
            {usersData?.length > 0 &&
              usersData?.filter(filterOptions)?.map(data => (
                <Option
                  key={`${data?.firstName};${data?.lastName};${data?.id};${data?.email}`}>
                  <div className={styles.image}>
                    <img src={data?.imageUrl} alt="" />
                    {data?.firstName ? (
                      <span>
                        {data?.firstName} {data?.lastName}
                      </span>
                    ) : (
                      <span>{data?.email}</span>
                    )}
                  </div>
                </Option>
              ))}
            {networkStatus === 2 && <Option key="Loading..." />}
          </Select>
          <span className={styles.hint}>
            Searches by name. The notification is addressed to the user IDs of
            whoever you select.
          </span>
        </div>
      ) : (
        <>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              Email addresses or user IDs
            </span>
            <Select
              mode="tags"
              // There is nothing to choose from: every value comes from what
              // the admin pasted or uploaded, so the dropdown stays shut.
              open={false}
              value={entries}
              // Commas, semicolons, tabs and newlines all split, so a column
              // copied straight out of a spreadsheet lands as separate chips.
              tokenSeparators={[',', ';', '\n', '\r', '\t']}
              placeholder="Paste addresses or user IDs separated by commas"
              allowClear
              suffixIcon={null}
              onChange={handleEntriesChange}
            />
            <span className={styles.hint}>
              Mix addresses and user IDs freely — each is recognised on its own
              and sent as `userEmails` or `userIds`. Names are not accepted
              here; use Search for users instead.
            </span>
          </div>

          <div className={styles.upload}>
            <Upload
              accept=".csv,.txt,text/csv"
              maxCount={1}
              showUploadList={false}
              beforeUpload={handleFile}>
              <button type="button" className={styles.uploadButton}>
                Upload a CSV of recipients
              </button>
            </Upload>
            <button
              type="button"
              className={styles.link}
              onClick={() => setShowFormat(!showFormat)}>
              {showFormat ? 'Hide format' : 'See the format'}
            </button>
          </div>

          {showFormat && (
            <div className={styles.format}>
              <span className={styles.formatIntro}>
                One email address or user ID per row. Save as CSV:
              </span>
              <pre className={styles.sample}>{SAMPLE_CSV}</pre>
              <button
                type="button"
                className={styles.link}
                onClick={downloadTemplate}>
                Download a template
              </button>
            </div>
          )}
        </>
      )}

      {mode === RECIPIENT_MODES.BULK && recipients.fileName && (
        <div className={styles.file}>
          <div className={styles.fileRow}>
            <span className={styles.fileName}>{recipients.fileName}</span>
            <button type="button" className={styles.link} onClick={clearUpload}>
              Dismiss
            </button>
          </div>
          <span className={styles.fileMeta}>
            Added to the list above. Remove individual recipients using their
            chips.
          </span>
        </div>
      )}

      {mode === RECIPIENT_MODES.BULK && invalidRows.length > 0 && (
        <div className={styles.invalid}>
          <div className={styles.fileRow}>
            <span>
              {invalidRows.length} row(s) skipped — not a valid email address or
              user ID.
            </span>
            <button
              type="button"
              className={styles.link}
              onClick={() => setShowInvalidRows(!showInvalidRows)}>
              {showInvalidRows ? 'Hide' : 'Show'}
            </button>
          </div>
          {showInvalidRows && (
            <ul className={styles.invalidList}>
              {invalidRows.slice(0, 20).map(row => (
                <li key={`${row.line}-${row.value}`}>
                  Line {row.line}: {row.value}
                </li>
              ))}
              {invalidRows.length > 20 && (
                <li>…and {invalidRows.length - 20} more</li>
              )}
            </ul>
          )}
        </div>
      )}

      {total > 0 && (
        <div className={styles.total}>
          Sending to {total} recipient(s)
          {/* Only the active mode is sent, so only it is broken down —
              counting the other input's entries here read as though they
              were going out too. */}
          {mode === RECIPIENT_MODES.BULK &&
            ` — ${emailCount} by email, ${idCount} by user ID`}
        </div>
      )}
    </div>
  );
};
